import React from 'react';
import styled, { keyframes } from 'styled-components';
import { PExtraSmall, PSmall } from '../elements/Text';
import { SmallTooltip } from '../Tooltip';

export const SIDE_NAVIGATION_WIDTH = 250;

const contentFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Wraps the content area of a sidebar page. Re-keying it on the current page
// replays the fade, so moving between sections reads as a transition rather
// than an instant swap.
export const PageTransition = styled.div`
  animation: ${contentFade} 250ms ease-out;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
`;

export const SideNavigationWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  min-width: ${SIDE_NAVIGATION_WIDTH}px;
  max-width: ${SIDE_NAVIGATION_WIDTH}px;
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${({ theme }) => theme.colors.navigationDivider};
  padding-bottom: 50px;
  overflow-y: auto;
  ::-webkit-scrollbar {
    display: none;
  }
`;

export const LeftToggleButton = styled.i.attrs({
  className: 'fa-solid fa-chevrons-left',
})`
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 16px;
  padding: 14px;
  transition: all 300ms ease-in-out;

  &:hover {
    cursor: pointer;
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  padding: 12px 10px 12px 24px;
  align-items: center;
`;

export const Text = styled(PSmall)`
  margin: 0;
  padding: 0;
  padding: 12px 0;
`;

export const Label = styled(PExtraSmall)`
  margin: 0;
  padding: 4px 10px 6px;
  font-weight: bold;
`;

export const Divider = styled.div`
  min-height: 1px;
  background: ${(props) => props.theme.colors.navigation};
`;

export const ClickableRow = styled.div<{
  isActive: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;

  opacity: ${(props) => (props.isDisabled ? 0.3 : 0.8)};
  ${(props) =>
    props.isDisabled
      ? `cursor: not-allowed;`
      : `&:hover {
    opacity: 1;
    cursor: pointer;
  }`}

  transition: all 300ms ease-in-out;

  border-radius: 8px;

  ${(props) =>
    props.isActive &&
    `
    opacity: 1;
    cursor: pointer;
    background-color: ${props.theme.colors.bgActive};
  `}

  ${(props) =>
    props?.isLoading &&
    `
    &:hover {
      cursor: wait;
    }
  `}
`;

export const Section = styled.div`
  padding: 20px 12px;
`;

export const UncheckedIcon = styled.i.attrs({
  className: 'fa-regular fa-circle',
})<{ isActive: boolean }>`
  color: ${(props) => props.theme.colors.textRegular};
  font-size: 16px;
  background-color: ${(props) =>
    props.isActive ? props.theme.colors.navigation : props.theme.colors.bgPage};
  border-radius: 50%;
  border: 2px solid
    ${(props) => (props.isActive ? 'transparent' : props.theme.colors.bgPage)};
  transition: all 300ms ease-in-out;
  z-index: 1;
`;

export const ReviewIcon = styled.i.attrs({
  className: 'fa-solid fa-comment-captions',
})<{ xOffset: number }>`
  color: ${(props) => props.theme.colors.textRegular};
  font-size: 16px;
  ${(props) => props.xOffset && `transform: translateX(${props.xOffset}px)`};
  z-index: 0;
`;

export const AIIcon = styled.i.attrs({
  className: 'fa-solid fa-sparkles',
})`
  color: ${(props) => props.theme.colors.textRegular};
  font-size: 16px;
  z-index: 0;
`;

export const CheckedIcon = styled.i.attrs<{
  isActive: boolean;
  isCorrect: boolean;
  doesSatisfyAllRequirements: boolean | null;
}>(({ isCorrect }) => ({
  className: isCorrect
    ? 'fa-solid fa-check-circle'
    : 'fa-solid fa-times-circle',
}))<{
  isActive: boolean;
  isCorrect: boolean;
  doesSatisfyAllRequirements: boolean | null;
}>`
  color: ${(props) =>
    props.isCorrect && props.doesSatisfyAllRequirements
      ? props.theme.colors.primary
      : props.theme.colors.secondary};
  font-size: 16px;
  border-radius: 50%;
  border: 2px solid
    ${(props) =>
      props.isActive
        ? props.theme.colors.navigation
        : props.theme.colors.bgPage};
  transition: all 300ms ease-in-out;
  z-index: 1;
`;

export const IconsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: -10px;
`;

export const ToggleButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  height: 64px;
  z-index: 1;
`;

export const RightToggleButton = styled.i.attrs({
  className: 'fa-solid fa-chevrons-right',
})`
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 16px;
  padding: 24px;
  transition: all 300ms ease-in-out;
  position: sticky;

  &:hover {
    cursor: pointer;
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

export const Icon = styled.i`
  color: ${(props) => props.theme.colors.textRegular};
  font-size: 16px;
  z-index: 0;
`;

export const RowWithTooltip = ({
  children,
  tooltip,
  isActive,
  isLoading,
  isDisabled,
  onClick,
  id,
}: {
  children: React.ReactNode;
  tooltip?: string;
  isActive: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  id: string;
  onClick?: () => void;
}) => {
  return (
    <React.Fragment>
      <ClickableRow
        isActive={isActive}
        isLoading={isLoading}
        isDisabled={isDisabled}
        className={`side-nav-row-${id}`}
        onClick={isDisabled ? undefined : onClick}
      >
        {children}
      </ClickableRow>
      {tooltip && (
        <SmallTooltip anchorSelect={`.side-nav-row-${id}`}>
          {tooltip}
        </SmallTooltip>
      )}
    </React.Fragment>
  );
};
